import { HttpError } from '@littlebird/auth-client';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';

import { backendApis } from '@/backendApis';
import { FEATURES, hasFeature } from '@/features/feature-flags';
import {
  supportedOnThisDevice,
  type CachedConnectedAccount,
  type EmailDraft,
  type IntegrationInfo,
  type IntegrationSuggestion,
} from '@/features/integrations/integrations.types';
import { inBrowser, inElectron } from '@/utils/capabilities';

// Query key factory
export const integrationKeys = {
  all: ['integrations'] as const,
  available: () => [...integrationKeys.all, 'available'] as const,
  lists: () => [...integrationKeys.all, 'list'] as const,
  list: (toolkit?: string) =>
    [...integrationKeys.lists(), { toolkit }] as const,
  status: (toolkit: string) =>
    [...integrationKeys.all, 'status', toolkit] as const,
  onDeviceMcpCatalog: () =>
    [...integrationKeys.all, 'on-device-catalog'] as const,
  suggestions: () => [...integrationKeys.all, 'suggestions'] as const,
  emailDraft: (draftId: string, toolkitName: string, accountLabel: string) =>
    [
      ...integrationKeys.all,
      'email-draft',
      toolkitName,
      accountLabel,
      draftId,
    ] as const,
};

export const availableForThisDevice = (
  integrations: IntegrationInfo[]
): IntegrationInfo[] =>
  integrations.filter((integration) =>
    supportedOnThisDevice(integration.platforms)
  );

/**
 * Hook to fetch available integrations for the user
 */
export const availableIntegrationsQueryOptions = () =>
  queryOptions({
    queryKey: integrationKeys.available(),
    queryFn: () => backendApis.integrations.getAvailableIntegrations(),
  });

export function useAvailableIntegrationsQuery(
  includeUnsupportedPlatforms = false
) {
  return useQuery({
    ...availableIntegrationsQueryOptions(),
    select: includeUnsupportedPlatforms ? undefined : availableForThisDevice,
  });
}

export const onDeviceMcpCatalogQueryOptions = () =>
  queryOptions({
    queryKey: integrationKeys.onDeviceMcpCatalog(),
    queryFn: () => backendApis.integrations.getOnDeviceMcpCatalog(),
    // Changes only on a backend deploy; an hour keeps the modal snappy on reopen.
    staleTime: 60 * 60 * 1000,
  });

/** Built-in on-device MCP servers available to install */
export function useOnDeviceMcpCatalogQuery(
  enabled: boolean,
  includeBrowser = false
) {
  return useQuery({
    ...onDeviceMcpCatalogQueryOptions(),
    enabled: enabled && (includeBrowser || !inBrowser()),
  });
}

/** Floor between refetches of the Home suggestions, which the server recomputes on every read */
export const INTEGRATION_SUGGESTIONS_REFRESH_INTERVAL_MS = 60_000;

export function useIntegrationSuggestionsQuery(enabled = true) {
  return useQuery({
    queryKey: integrationKeys.suggestions(),
    queryFn: () =>
      backendApis.integrations.getIntegrationSuggestions(
        hasFeature(FEATURES.ON_DEVICE_INTEGRATION_SUGGESTIONS) &&
          hasFeature(FEATURES.CUSTOM_LOCAL_MCP) &&
          inElectron()
      ),
    staleTime: INTEGRATION_SUGGESTIONS_REFRESH_INTERVAL_MS,
    enabled,
  });
}

/** Dismiss a suggestion; drops it from the cache right away */
export function useDismissIntegrationSuggestionMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (toolkitSlug: string) =>
      backendApis.integrations.dismissIntegrationSuggestion(toolkitSlug),
    onMutate: async (toolkitSlug) => {
      await queryClient.cancelQueries({
        queryKey: integrationKeys.suggestions(),
      });
      queryClient.setQueryData<IntegrationSuggestion[]>(
        integrationKeys.suggestions(),
        (suggestions) =>
          suggestions?.filter((s) => s.toolkit_slug !== toolkitSlug)
      );
    },
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: integrationKeys.suggestions(),
      });
    },
  });
}

// Tracks the most recent probe generation per toolkit_name so stale probes
// from an earlier fetch cannot overwrite fresher cache state.
const probeGeneration = new Map<string | undefined, number>();

/**
 * Hook to fetch all connected integration accounts.
 */
export const connectedAccountsQueryOptions = (
  queryClient: QueryClient,
  toolkitName?: string
) =>
  queryOptions({
    queryKey: integrationKeys.list(toolkitName),
    // No focus refetch: each fetch also runs a live probe
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    queryFn: async (): Promise<CachedConnectedAccount[]> => {
      const accounts =
        await backendApis.integrations.getConnectedAccounts(toolkitName);

      const gen = (probeGeneration.get(toolkitName) ?? 0) + 1;
      probeGeneration.set(toolkitName, gen);

      const accountKey = (a: CachedConnectedAccount) =>
        `${a.toolkit_name}:${a.account_label}`;
      // Guard with `gen` so a probe that outlives a subsequent fetch is ignored.
      backendApis.integrations
        .getConnectedAccounts(toolkitName, true)
        .then((probed) => {
          if (probeGeneration.get(toolkitName) !== gen) return;
          const probedByKey = new Map(probed.map((a) => [accountKey(a), a]));
          queryClient.setQueryData<CachedConnectedAccount[]>(
            integrationKeys.list(toolkitName),
            (current) => {
              if (!current) return probed;
              return current.map((a) => {
                const p = probedByKey.get(accountKey(a));
                if (!p || p.connection_status === a.connection_status) return a;
                return { ...a, connection_status: p.connection_status };
              });
            }
          );
        })
        .catch(() => {});

      return accounts;
    },
  });

export function useConnectedAccountsQuery(
  toolkit_name?: string,
  enabled = true
) {
  const queryClient = useQueryClient();

  return useQuery({
    ...connectedAccountsQueryOptions(queryClient, toolkit_name),
    enabled,
  });
}

/**
 * Hook to disconnect an integration
 */
export function useDisconnectIntegrationMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (accountId: string) =>
      backendApis.integrations.disconnectIntegration(accountId),
    onSuccess: () => {
      // Invalidate all integration queries; let server state be source of truth
      queryClient.invalidateQueries({ queryKey: integrationKeys.all });
    },
  });
}

/**
 * Hook to send a previously created email draft
 */
export function useSendDraftMutation() {
  return useMutation({
    mutationFn: ({
      draftId,
      toolkitName,
      accountLabel,
    }: {
      draftId: string;
      toolkitName: string;
      accountLabel: string;
    }) =>
      backendApis.integrations.sendDraft(draftId, toolkitName, accountLabel),
  });
}

/**
 * Hook to fetch the details of an email draft (subject, body, recipients)
 */
export function useEmailDraftQuery(
  draftId: string,
  toolkitName: string,
  accountLabel: string
) {
  return useQuery({
    queryKey: integrationKeys.emailDraft(draftId, toolkitName, accountLabel),
    queryFn: () =>
      backendApis.integrations.getDraft(draftId, toolkitName, accountLabel),
    staleTime: Infinity,
    retry: (failureCount, error) => {
      if (error instanceof HttpError && error.status === 404) return false;
      return failureCount < 3;
    },
  });
}

/**
 * Hook to update an email draft's content and invalidate its cached details
 */
export function useUpdateDraftMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      draftId,
      toolkitName,
      accountLabel,
      ...updates
    }: {
      draftId: string;
      toolkitName: string;
      accountLabel: string;
    } & EmailDraft) =>
      backendApis.integrations.updateDraft(
        draftId,
        toolkitName,
        accountLabel,
        updates
      ),
    onSuccess: (_, { draftId, toolkitName, accountLabel }) => {
      queryClient.invalidateQueries({
        queryKey: integrationKeys.emailDraft(
          draftId,
          toolkitName,
          accountLabel
        ),
      });
    },
  });
}

/**
 * Hook to update account label (for multi-account support)
 */
export function useUpdateAccountLabelMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      accountId,
      newLabel,
    }: {
      accountId: string;
      newLabel: string;
    }) => backendApis.integrations.updateAccountLabel(accountId, newLabel),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.all });
    },
  });
}
